<template>
	<div class="stock_list" :class="displayStore.isDesktop && 'stock_list_desktop'">
		<div
			v-for="(item, index) in props.data"
			:key="index"
			class="list_container"
			:class="{
				'unsubscribe-desktop': props.subscribe === 0 && displayStore.isDesktop,
				'unsubscribe-mobile': props.subscribe === 0 && !displayStore.isDesktop,
				'hidden-mobile':
					props.subscribe === 0 && !displayStore.isDesktop && index >= 3 && index < props.data.length - 3,
			}"
		>
			<div class="list_item_wrap">
				<div
					v-if="props.subYn !== 0"
					class="bookmark_area"
					@click="onClickBookmark(item.event_pk, item.master_investment_pk, item.bookmark)"
				>
					<IconStar :icon-color="!item.bookmark ? '#D9D9D9' : '#FFB800'" />
				</div>
				<div v-else class="bookmark_area">
					<div class="bookmark_number">{{ index + 1 }}</div>
				</div>
				<div class="item_content">
					<div class="item_content_layout">
						<div v-if="props.subYn !== 0" class="idx">{{ index + 1 }}</div>
						<div class="content">

							<img
							v-if="item.file_path"
								:src="item.file_path ?? logoImg"
								alt="logo"
								loading="lazy"
								:class="item.event_name === '비공개' ? 'blur-img' : ''"
							/>
							<img v-else :src="logoImg" alt="logo" loading="lazy" :class="item.event_name === '비공개' ? 'blur-img' : ''"
							/>

							<div class="content_txt">
								<h4 :class="item.event_name === '비공개'  ? 'blur-text' : ''">
									{{ item.event_name }}
								</h4>
								<p>{{ item.event_ticker || item.event_code }}</p>
							</div>
						</div>
					</div>
					<button
						class="c_button primary-fill radius-full btn-research"
						@click="onClickResearch(item.event_pk, item.event_name)"
					>
						분석
					</button>
				</div>
			</div>
			<div v-if="!displayStore.isDesktop && props.subscribe === 0 && index === 2" class="item_img">
				<img src="@/assets/img/common/ellipsis.svg" alt="ellipsis" loading="lazy" />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';

import { useAdjustBookmarksApi } from '@/api/Interest';
import { GetMasterEventInvestmentData } from '@/api/master';
import logoImg from '@/assets/img/bi/bi.svg';
import {
	CANCEL,
	COMPANY_TITLE,
	CONFIRM_LOGIN,
	LOGIN_REQUIRED_PAGE,
	MESSAGE_CANCEL_COMPLETED,
	UNFAVORITE_PROMPT,
} from '@/constants/title';
import { useDisplayStore } from '@/stores/display';
import { useLoginPopupStore } from '@/stores/LoginPopup';
import { useModalStore } from '@/stores/modal';
import { usePopupStore } from '@/stores/popup';
import { useToastStore } from '@/stores/toast';
import { useTokenStore } from '@/stores/token';

import IconStar from '../atom/icon/IconStar.vue';

// -----variable-----
const props = defineProps<{
	data: GetMasterEventInvestmentData[];
	subYn?: number;
	subscribe?: number;
	masterPk: number;
}>();
const emit = defineEmits(['update:bookMarkRefetch']);

const router = useRouter();
const displayStore = useDisplayStore();
const popupStore = usePopupStore();
const toastStore = useToastStore();
const modalStore = useModalStore();
const tokenStore = useTokenStore();
const loginPopupStore = useLoginPopupStore();

const { mutateAsync: adjustBookmarks } = useAdjustBookmarksApi();

// -----function-----
const onClickBookmark = async (eventPk: number, investmentPk: number, bookmark: number) => {
	const params = [
		{
			event_pk: eventPk,
			master_investment_pk: investmentPk,
			master_pk: props.masterPk,
		},
	];
	if (!tokenStore.isLogin) {
		loginPopupStore.openPopup({
			icon: 'alert',
			content: LOGIN_REQUIRED_PAGE,
			confirm: () => router.push('/auth/login'),
			confirmText: CONFIRM_LOGIN,
			cancelText: CANCEL,
		});
		return;
	}

	if (bookmark) {
		popupStore.openPopup({
			content: COMPANY_TITLE,
			subtext: UNFAVORITE_PROMPT,
			confirm: async () => {
				const res = await adjustBookmarks({
					bookmarks: params,
				});
				if (res.data.success) {
					emit('update:bookMarkRefetch');
					toastStore.openToast({
						subContent: COMPANY_TITLE,
						content: MESSAGE_CANCEL_COMPLETED,
					});
				}
			},
		});
	} else {
		const res = await adjustBookmarks({
			bookmarks: params,
		});
		if (res.data.success) {
			emit('update:bookMarkRefetch');
		}
	}
};

const onClickResearch = async (pk: number, eventName: string) => {
	if (!tokenStore.isLogin) {
		document.body.classList.add('hidden');
		loginPopupStore.openPopup({
			icon: 'alert',
			content: LOGIN_REQUIRED_PAGE,
			confirm: () => router.push('/auth/login'),
			confirmText: CONFIRM_LOGIN,
			cancelText: CANCEL,
		});
		return;
	}

	if (eventName === '비공개') {
		document.body.classList.add('hidden');
		modalStore.openModal('GuideModal');
		return;
	}
	router.push({ path: `/report/kr/${pk}`, query: { sub: props.subYn } });
};
</script>

<style scoped lang="scss">
.stock_list {
	display: grid;
	gap: 10px;
	.item_img {
		display: flex;
		justify-content: center;
		align-items: center;
		img {
			width: 0.8rem;
			height: 6rem;
			margin-top: 1rem;
		}
	}
}
.hidden-mobile {
	display: none;
}
.list_container {
	min-width: 0;
}
.stock_list_desktop .list_container:nth-child(1) {
	grid-column: span 2;
}
.stock_list_desktop .list_container:nth-child(n + 2) {
	grid-column: span 1;
	width: 33.5rem;
}
.list_item_wrap {
	display: flex;
	min-width: 0;
	.bookmark_area {
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 0.8rem;
		border-radius: 8px 0px 0px 8px;
		background: var(--color-bg-light);
		cursor: pointer;
		.bookmark_number {
			font-size: var(--font-s-small);
			font-weight: var(--font-w-bold);
			line-height: normal;
			width: 1.6rem;
			text-align: center;
		}
		img {
			width: 2.4rem;
			height: 2.4rem;
		}
	}
	.item_content {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		border-radius: 0px 8px 8px 0px;
		background: var(--color-bg-mid);
		min-width: 0;
		.item_content_layout {
			display: flex;
			justify-content: start;
			align-items: center;
			gap: 0.8rem;
			padding: 1.6rem 1rem;
			min-width: 0;
		}
		.idx {
			width: 2.4rem;
			text-align: center;
			font-size: var(--font-s-small);
			font-weight: var(--font-w-bold);
			line-height: normal;
		}
		.content {
			display: flex;
			justify-content: start;
			align-items: center;
			gap: 0.8rem;
			min-width: 0;
			img {
				width: 3.2rem;
				height: 3.2rem;
				border-radius: 50%;
				&.blur-img {
					filter: blur(5px) grayscale(100%);
				}
			}
			.content_txt {
				min-width: 0;
				h4 {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					font-size: var(--font-s-mid);
					font-weight: var(--font-w-semi-bold);
					line-height: normal;
					&.blur-text {
						filter: blur(5px);
					}
				}
				p {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					color: #c6c6d6;
					font-size: var(--font-s-small);
					font-weight: var(--font-w-light);
					line-height: normal;
				}
			}
		}
		.btn-research {
			display: flex;
			width: 5.8rem;
			height: auto; // c_button의 height unset
			padding: 0.6rem 1.6rem;
			justify-content: center;
			align-items: center;
			gap: 10px;
			margin-right: 1rem;
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-semi-bold);
			line-height: normal;
		}
	}
}
</style>
